import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSettings, useUpdateSettings } from '@/api/hooks/useAdminSettings';
import { useDashboardSummary } from '@/api/hooks/useAdmin';
import SportsManagement from '@/components/admin/SportsManagement';
import PaymentsSection from '@/components/admin/PaymentsSection';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Info, MapPin, Plus, Trash2 } from 'lucide-react';

type Policies = Record<string, unknown>;

function getNestedValue(obj: Policies, path: string): unknown {
  return path.split('.').reduce((o: unknown, k) => (o as Record<string, unknown>)?.[k], obj);
}

function setNestedValue(obj: Policies, path: string, value: unknown): Policies {
  const keys = path.split('.');
  const result = { ...obj };
  let current: Record<string, unknown> = result;
  for (let i = 0; i < keys.length - 1; i++) {
    current[keys[i]] = { ...(current[keys[i]] as Record<string, unknown> || {}) };
    current = current[keys[i]] as Record<string, unknown>;
  }
  current[keys[keys.length - 1]] = value;
  return result;
}

function FieldLabel({ label, tooltip }: { label: string; tooltip?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <Label>{label}</Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger>
            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

const NOTIFICATION_TYPES = [
  'bookingConfirmation',
  'sessionReminder',
  'sessionWarning',
  'sessionComplete',
  'cancellation',
  'noShow',
  'extension',
  'registrationInvite',
  'creditTopUp',
  'vipEarned',
] as const;

const STAFF_PERMISSIONS = [
  'canOverrideUnits',
  'canResetEquipment',
  'canUnlockDoors',
  'canAcknowledgeAlerts',
  'canResolveAlerts',
  'canModifyPricing',
  'canViewReports',
  'canModifySettings',
] as const;

export default function SettingsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const section = location.hash?.slice(1) || 'booking'; // default to booking
  const [settingsScope, setSettingsScope] = useState<string>('tenant');

  const { data: dashboardSummary } = useDashboardSummary();
  const locations = dashboardSummary?.locationBreakdown ?? [];

  const locationId = settingsScope !== 'tenant' ? settingsScope : undefined;
  const { data: settings, isLoading } = useSettings(locationId);
  const updateMutation = useUpdateSettings(locationId);

  const [operational, setOperational] = useState<Policies>({});
  const [cancellation, setCancellation] = useState<Policies>({});

  useEffect(() => {
    if (settings) {
      setOperational(settings.operationalPolicies || {});
      setCancellation(settings.cancellationPolicy || {});
    }
  }, [settings]);

  const saveSection = async (section: 'operational' | 'cancellation') => {
    try {
      if (section === 'operational') {
        await updateMutation.mutateAsync({ operationalPolicies: operational });
      } else {
        await updateMutation.mutateAsync({ cancellationPolicy: cancellation });
      }
      toast.success(t('admin.settings_saved'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getOp = (path: string, fallback: any = ''): any =>
    getNestedValue(operational, path) ?? fallback;

  const setOp = (path: string, value: unknown) =>
    setOperational((prev) => setNestedValue(prev, path, value));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-semibold">{t('admin.settings')}</h1>

          {locations.length > 1 && (
            <Select value={settingsScope} onValueChange={(v) => setSettingsScope(v ?? 'tenant')}>
              <SelectTrigger>
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm">
                  {settingsScope === 'tenant'
                    ? t('admin.tenant_defaults')
                    : locations.find((l) => l.locationId === settingsScope)?.locationName ?? ''}
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tenant">{t('admin.tenant_defaults')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.locationId} value={loc.locationId}>{loc.locationName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {settingsScope !== 'tenant' && (
          <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700">
            {t('admin.location_settings_note')}
          </div>
        )}

        {section === 'booking' && (<>
        <div id="booking" className="scroll-mt-4" />
        {/* Booking Policy */}
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.booking_policy')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel label={t('admin.stale_threshold')} tooltip={t('admin.tooltip_stale_threshold')} />
                <Input
                  type="number" min={1}
                  value={getOp('bookingPolicy.staleThresholdMinutes', 30) as number}
                  onChange={(e) => setOp('bookingPolicy.staleThresholdMinutes', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.max_advance_days')} tooltip={t('admin.tooltip_max_advance')} />
                <Input
                  type="number" min={1}
                  value={getOp('bookingPolicy.maxAdvanceBookingDays', 14) as number}
                  onChange={(e) => setOp('bookingPolicy.maxAdvanceBookingDays', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Session Policy */}
        <Card className="border-l-4 border-l-primary">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.session_policy')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel label={t('admin.grace_period')} tooltip={t('admin.tooltip_grace_period')} />
                <Input
                  type="number" min={0}
                  value={getOp('sessionActivationPolicy.gracePeriodMinutes', 10) as number}
                  onChange={(e) => setOp('sessionActivationPolicy.gracePeriodMinutes', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.no_show_minutes')} tooltip={t('admin.tooltip_no_show')} />
                <Input
                  type="number" min={0}
                  value={getOp('noShowPolicy.detectAfterMinutes', 15) as number}
                  onChange={(e) => setOp('noShowPolicy.detectAfterMinutes', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.no_show_risk_threshold')} tooltip={t('admin.tooltip_no_show_risk')} />
                <Input
                  type="number" min={1}
                  value={getOp('noShowPolicy.noShowRiskThreshold', 2) as number}
                  onChange={(e) => setOp('noShowPolicy.noShowRiskThreshold', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.deposit_required_threshold')} tooltip={t('admin.tooltip_deposit_threshold')} />
                <Input
                  type="number" min={1}
                  value={getOp('noShowPolicy.depositRequiredThreshold', 3) as number}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    const risk = getOp('noShowPolicy.noShowRiskThreshold', 2) as number;
                    if (val < risk) return;
                    setOp('noShowPolicy.depositRequiredThreshold', val);
                  }}
                  className="mt-1"
                />
                {(getOp('noShowPolicy.depositRequiredThreshold', 3) as number) < (getOp('noShowPolicy.noShowRiskThreshold', 2) as number) && (
                  <p className="text-xs text-destructive mt-1">{t('admin.deposit_threshold_error')}</p>
                )}
              </div>
              <div>
                <FieldLabel label={t('admin.deposit_amount')} tooltip={t('admin.tooltip_deposit_amount')} />
                <Input
                  type="number" min={0} step={1}
                  value={getOp('noShowPolicy.depositAmount', 10) as number}
                  onChange={(e) => setOp('noShowPolicy.depositAmount', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <FieldLabel label={t('admin.warning_thresholds')} tooltip={t('admin.tooltip_warnings')} />
                <Input
                  value={((getOp('sessionWarningPolicy.warningThresholds', [5, 2, 1]) as number[]) || []).join(', ')}
                  onChange={(e) => {
                    const vals = e.target.value.split(',').map((v) => Number(v.trim())).filter((v) => v > 0);
                    setOp('sessionWarningPolicy.warningThresholds', vals);
                  }}
                  placeholder="5, 2, 1"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">{t('admin.warning_hint')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        </>)}

        {section === 'loyalty' && (<>
        <div id="loyalty" className="scroll-mt-4" />
        {/* VIP Policy */}
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.vip_policy')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel label={t('admin.vip_discount_percent')} tooltip={t('admin.tooltip_vip_discount')} />
                <Input
                  type="number" min={0} max={100}
                  value={getOp('vipPolicy.discountPercent', 10) as number}
                  onChange={(e) => setOp('vipPolicy.discountPercent', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.vip_early_access')} tooltip={t('admin.tooltip_vip_early_access')} />
                <div className="flex items-center gap-1.5 mt-1">
                  <Input
                    type="number" min={0}
                    value={getOp('vipPolicy.earlyAccessMinutes', 0) as number}
                    onChange={(e) => setOp('vipPolicy.earlyAccessMinutes', Number(e.target.value))}
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                </div>
              </div>
              <div>
                <FieldLabel label={t('admin.vip_booking_window')} tooltip={t('admin.tooltip_vip_booking_window')} />
                <div className="flex items-center gap-1.5 mt-1">
                  <Input
                    type="number" min={1}
                    value={getOp('vipPolicy.vipBookingWindowDays', 30) as number}
                    onChange={(e) => setOp('vipPolicy.vipBookingWindowDays', Number(e.target.value))}
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{t('admin.days')}</span>
                </div>
              </div>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <FieldLabel label={t('admin.auto_vip')} tooltip={t('admin.tooltip_auto_vip')} />
              <Switch
                checked={getOp('vipPolicy.autoVipEnabled', true) as boolean}
                onCheckedChange={(v) => setOp('vipPolicy.autoVipEnabled', v)}
              />
            </div>
            {getOp('vipPolicy.autoVipEnabled', true) && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel label={t('admin.auto_vip_criteria')} />
                  <select
                    value={getOp('vipPolicy.autoVipCriteria', 'BOTH') as string}
                    onChange={(e) => setOp('vipPolicy.autoVipCriteria', e.target.value)}
                    className="mt-1 h-8 w-full rounded-lg border border-input bg-transparent px-2 text-sm outline-none"
                  >
                    <option value="BOOKINGS">{t('admin.criteria_bookings')}</option>
                    <option value="SPEND">{t('admin.criteria_spend')}</option>
                    <option value="BOTH">{t('admin.criteria_both')}</option>
                  </select>
                </div>
                <div>
                  <FieldLabel label={t('admin.auto_vip_window')} />
                  <div className="flex items-center gap-1.5 mt-1">
                    <Input type="number" min={1}
                      value={getOp('vipPolicy.autoVipWindowMonths', 6) as number}
                      onChange={(e) => setOp('vipPolicy.autoVipWindowMonths', Number(e.target.value))} />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t('admin.months')}</span>
                  </div>
                </div>
                {(['BOOKINGS', 'BOTH'] as string[]).includes(getOp('vipPolicy.autoVipCriteria', 'BOTH') as string) && (
                  <div>
                    <FieldLabel label={t('admin.auto_vip_booking_threshold')} />
                    <Input type="number" min={1}
                      value={getOp('vipPolicy.autoVipBookingThreshold', 10) as number}
                      onChange={(e) => setOp('vipPolicy.autoVipBookingThreshold', Number(e.target.value))}
                      className="mt-1" />
                  </div>
                )}
                {(['SPEND', 'BOTH'] as string[]).includes(getOp('vipPolicy.autoVipCriteria', 'BOTH') as string) && (
                  <div>
                    <FieldLabel label={t('admin.auto_vip_spend_threshold')} />
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input type="number" min={0} step={50}
                        value={getOp('vipPolicy.autoVipSpendThreshold', 500) as number}
                        onChange={(e) => setOp('vipPolicy.autoVipSpendThreshold', Number(e.target.value))} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit Policy */}
        <Card className="border-l-4 border-l-amber-400">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.credit_policy')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <FieldLabel label={t('admin.credits_enabled')} tooltip={t('admin.tooltip_credits_enabled')} />
              <Switch
                checked={getOp('creditPolicy.enabled', true) as boolean}
                onCheckedChange={(v) => setOp('creditPolicy.enabled', v)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel label={t('admin.min_topup')} tooltip={t('admin.tooltip_min_topup')} />
                <Input
                  type="number" min={0}
                  value={getOp('creditPolicy.minimumTopUp', 50) as number}
                  onChange={(e) => setOp('creditPolicy.minimumTopUp', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.credit_expiry_months')} tooltip={t('admin.tooltip_credit_expiry')} />
                <Input
                  type="number" min={1}
                  value={getOp('creditPolicy.creditExpiryMonths', 12) as number}
                  onChange={(e) => setOp('creditPolicy.creditExpiryMonths', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <FieldLabel label={t('admin.allow_partial_credit')} tooltip={t('admin.tooltip_partial_credit')} />
              <Switch
                checked={getOp('creditPolicy.allowPartialCredit', true) as boolean}
                onCheckedChange={(v) => setOp('creditPolicy.allowPartialCredit', v)}
              />
            </div>
            {/* Tier editor */}
            <div>
              <p className="text-xs font-semibold mb-2">{t('admin.credit_tiers')}</p>
              <div className="space-y-2">
                {((getOp('creditPolicy.tiers', []) as { name: string; amount: number; bonusPercent: number; totalCredit: number }[]) || []).map((tier, i) => {
                  const tiers = (getOp('creditPolicy.tiers', []) as { name: string; amount: number; bonusPercent: number; totalCredit: number }[]) || [];
                  const updateTier = (field: string, value: string | number) => {
                    const updated = [...tiers];
                    updated[i] = { ...updated[i], [field]: value };
                    if (field === 'amount' || field === 'bonusPercent') {
                      const amt = field === 'amount' ? Number(value) : updated[i].amount;
                      const bp = field === 'bonusPercent' ? Number(value) : updated[i].bonusPercent;
                      updated[i].totalCredit = Math.round(amt * (1 + bp / 100) * 100) / 100;
                    }
                    setOp('creditPolicy.tiers', updated);
                  };
                  return (
                    <div key={i} className="grid grid-cols-[1fr_80px_80px_90px_32px] gap-2 items-center">
                      <input
                        type="text" value={tier.name}
                        onChange={(e) => updateTier('name', e.target.value)}
                        className="h-8 rounded-md border border-input bg-transparent px-2 text-sm outline-none"
                        placeholder={t('admin.tier_name')}
                      />
                      <input
                        type="number" min={0} value={tier.amount}
                        onChange={(e) => updateTier('amount', Number(e.target.value))}
                        className="h-8 rounded-md border border-input bg-transparent px-2 text-sm outline-none text-right"
                        placeholder="$"
                      />
                      <input
                        type="number" min={0} max={100} value={tier.bonusPercent}
                        onChange={(e) => updateTier('bonusPercent', Number(e.target.value))}
                        className="h-8 rounded-md border border-input bg-transparent px-2 text-sm outline-none text-right"
                        placeholder="%"
                      />
                      <span className="text-xs font-mono text-primary font-semibold text-right">${tier.totalCredit}</span>
                      <button
                        onClick={() => {
                          if (tiers.length <= 1) return;
                          const updated = tiers.filter((_, idx) => idx !== i);
                          setOp('creditPolicy.tiers', updated);
                        }}
                        disabled={tiers.length <= 1}
                        className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-destructive/10 disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-[10px] text-muted-foreground">{t('admin.tier_columns')}</p>
                <Button
                  variant="outline" size="sm"
                  onClick={() => {
                    const tiers = (getOp('creditPolicy.tiers', []) as { name: string; amount: number; bonusPercent: number; totalCredit: number }[]) || [];
                    setOp('creditPolicy.tiers', [...tiers, { name: '', amount: 100, bonusPercent: 0, totalCredit: 100 }]);
                  }}
                >
                  <Plus className="mr-1 h-3 w-3" /> {t('admin.add_tier')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </>)}

        {section === 'cancellation' && (<>
        <div id="cancellation" className="scroll-mt-4" />
        {/* Cancellation & Refund */}
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.cancellation_policy')}</h2>
              <Button size="sm" onClick={() => saveSection('cancellation')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel label={t('admin.cancellation_grace')} tooltip={t('admin.tooltip_cancel_grace')} />
                <Input
                  type="number" min={0}
                  value={(cancellation.gracePeriodMinutes as number) ?? 60}
                  onChange={(e) => setCancellation((prev) => ({ ...prev, gracePeriodMinutes: Number(e.target.value) }))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.credit_bonus')} tooltip={t('admin.tooltip_credit_bonus')} />
                <Input
                  type="number" min={0} max={100}
                  value={(cancellation.creditBonusPercent as number) ?? 0}
                  onChange={(e) => setCancellation((prev) => ({ ...prev, creditBonusPercent: Number(e.target.value) }))}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        </>)}

        {section === 'notifications' && (<>
        <div id="notifications" className="scroll-mt-4" />
        {/* Notification Preferences */}
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.notification_preferences')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            {settings?.smsPlanInfo && (
              <div className={`rounded-md border px-3 py-2 text-xs ${settings.smsPlanInfo.enabled ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                {!settings.smsPlanInfo.enabled
                  ? t('admin.sms_plan_disabled')
                  : settings.smsPlanInfo.quotaMonthly === 0
                    ? `${t('admin.sms_plan_unlimited')} (${settings.smsPlanInfo.planTier})`
                    : `${t('admin.sms_plan_quota')}: ${settings.smsPlanInfo.usageMonthly} / ${settings.smsPlanInfo.quotaMonthly} (${settings.smsPlanInfo.planTier})`
                }
              </div>
            )}
            <p className="text-xs text-muted-foreground">{t('admin.notification_hint')}</p>
            <div className="space-y-2">
              {NOTIFICATION_TYPES.map((type) => {
                const enabled = getOp(`notificationPreferences.${type}.enabled`, true) as boolean;
                const emailOn = getOp(`notificationPreferences.${type}.email`, true) as boolean;
                const smsOn = getOp(`notificationPreferences.${type}.sms`, false) as boolean;
                const smsAllowed = settings?.smsPlanInfo?.enabled ?? false;
                const checkedCount = (emailOn ? 1 : 0) + (smsOn && smsAllowed ? 1 : 0);
                const emailIsLast = enabled && emailOn && checkedCount === 1;
                const smsIsLast = enabled && smsOn && smsAllowed && checkedCount === 1;
                return (
                  <div key={type} className="flex items-center justify-between rounded-md border p-3 gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Switch
                        checked={enabled}
                        onCheckedChange={(v) => setOp(`notificationPreferences.${type}.enabled`, v)}
                      />
                      <span className="text-sm truncate">{t(`admin.notif_${type}`)}</span>
                    </div>
                    {enabled && (
                      <div className="flex items-center gap-3 shrink-0">
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground" title={emailIsLast ? t('admin.last_channel_warning') : undefined}>
                          <input
                            type="checkbox"
                            checked={emailOn}
                            onChange={(e) => setOp(`notificationPreferences.${type}.email`, e.target.checked)}
                            disabled={emailIsLast}
                            className="h-3.5 w-3.5 rounded border disabled:opacity-50"
                          />
                          Email
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground" title={!smsAllowed ? t('admin.sms_plan_required') : smsIsLast ? t('admin.last_channel_warning') : undefined}>
                          <input
                            type="checkbox"
                            checked={smsOn && smsAllowed}
                            onChange={(e) => setOp(`notificationPreferences.${type}.sms`, e.target.checked)}
                            disabled={!smsAllowed || smsIsLast}
                            className="h-3.5 w-3.5 rounded border disabled:opacity-50"
                          />
                          SMS
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        </>)}

        {section === 'staff' && (<>
        <div id="staff" className="scroll-mt-4" />
        {/* Alert Escalation */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.alert_escalation')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <FieldLabel label={t('admin.p1_escalation')} tooltip={t('admin.tooltip_p1_escalation')} />
                <Input
                  type="number" min={1}
                  value={getOp('alertEscalationPolicy.p1EscalationMinutes', 5) as number}
                  onChange={(e) => setOp('alertEscalationPolicy.p1EscalationMinutes', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.p2_escalation')} tooltip={t('admin.tooltip_p2_escalation')} />
                <Input
                  type="number" min={1}
                  value={getOp('alertEscalationPolicy.p2EscalationMinutes', 15) as number}
                  onChange={(e) => setOp('alertEscalationPolicy.p2EscalationMinutes', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
              <div>
                <FieldLabel label={t('admin.max_escalation')} />
                <Input
                  type="number" min={1}
                  value={getOp('alertEscalationPolicy.maxEscalationLevel', 3) as number}
                  onChange={(e) => setOp('alertEscalationPolicy.maxEscalationLevel', Number(e.target.value))}
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Staff Permissions */}
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.staff_permissions')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <p className="text-xs text-muted-foreground">{t('admin.staff_permissions_hint')}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {STAFF_PERMISSIONS.map((perm) => (
                <div key={perm} className="flex items-center justify-between rounded-md border p-3">
                  <Label className="text-sm">{t(`admin.perm_${perm}`)}</Label>
                  <Switch
                    checked={!!getOp(`staffPermissions.${perm}`, false)}
                    onCheckedChange={(checked) => setOp(`staffPermissions.${perm}`, checked)}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sports Management */}
        <SportsManagement locationId={locationId} />
        </>)}

        {section === 'payments' && (<>
        <div id="payments" className="scroll-mt-4" />
        {/* Stripe Connect */}
        <PaymentsSection />
        </>)}

        {section === 'locales' && (<>
        <div id="locales" className="scroll-mt-4" />
        {/* PR V.c: tenant-level supportedLocales policy. Controls
            whether the FR tab shows in admin sport/addon forms and
            whether the "not yet translated" badge surfaces. English
            is always on (canonical); French opts in. */}
        <Card className="border-l-4 border-l-sky-500">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">{t('admin.locales_section_title')}</h2>
              <Button size="sm" onClick={() => saveSection('operational')} disabled={updateMutation.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
            <Separator />
            <p className="text-xs text-muted-foreground">{t('admin.locales_section_hint')}</p>
            <div className="space-y-2">
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked disabled className="h-4 w-4 rounded border-neutral-300" />
                <span>{t('admin.locale_english')}</span>
              </label>
              <label className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={Array.isArray(getOp('supportedLocales', ['en-CA']))
                    ? (getOp('supportedLocales', ['en-CA']) as string[]).includes('fr-CA')
                    : false}
                  onChange={(e) => {
                    const current = (getOp('supportedLocales', ['en-CA']) as string[]) ?? ['en-CA'];
                    const next = e.target.checked
                      ? Array.from(new Set([...current, 'fr-CA']))
                      : current.filter((l) => l !== 'fr-CA');
                    setOp('supportedLocales', next.length > 0 ? next : ['en-CA']);
                  }}
                  className="h-4 w-4 rounded border-neutral-300"
                />
                <span>{t('admin.locales_french_supported')}</span>
              </label>
            </div>
          </CardContent>
        </Card>
        </>)}

      </div>
    </TooltipProvider>
  );
}
